import React, { useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Workflow, Mic, Upload, Settings, X, Sparkles,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useUIStore } from '@/stores/ui.store';

interface QuickAction {
  id: string;
  label: string;
  description: string;
  icon: React.ElementType;
  shortcut?: string;
  path?: string;
  color: string;
}

const ACTIONS: QuickAction[] = [
  {
    id: 'new-eduflow',
    label: 'Nouvelle formation',
    description: 'Créer une nouvelle formation',
    icon: Workflow,
    shortcut: '⌘E',
    path: '/eduflow/new',
    color: 'from-secondary-500 to-secondary-600',
  },
  {
    id: 'new-audio',
    label: 'quickActions.newAudio',
    description: 'quickActions.newAudioDesc',
    icon: Mic,
    path: '/sounds/new',
    color: 'from-accent-500 to-accent-600',
  },
  {
    id: 'import',
    label: 'quickActions.import',
    description: 'quickActions.importDesc',
    icon: Upload,
    shortcut: '⌘I',
    path: '/import',
    color: 'from-amber-500 to-orange-500',
  },
  {
    id: 'quick-settings',
    label: 'quickActions.settings',
    description: 'quickActions.settingsDesc',
    icon: Settings,
    shortcut: '⌘,',
    path: '/settings',
    color: 'from-gray-500 to-gray-600',
  },
];

export function QuickActions() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { toggleQuickActions } = useUIStore();

  const handleClose = useCallback(() => {
    toggleQuickActions();
  }, [toggleQuickActions]);

  const handleAction = useCallback(
    (action: QuickAction) => {
      if (action.path) {
        navigate(action.path);
      }
      handleClose();
    },
    [navigate, handleClose]
  );

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleClose();
      }
      if (e.key === 'A' && (e.metaKey || e.ctrlKey) && e.shiftKey) {
        e.preventDefault();
        handleClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleClose]);

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-50 flex items-center justify-center"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        {/* Backdrop */}
        <div
          className="absolute inset-0 bg-black/50 backdrop-blur-sm"
          onClick={handleClose}
          aria-hidden="true"
        />

        {/* Panel */}
        <motion.div
          className="relative w-full max-w-lg mx-4 rounded-2xl border border-[#E5DDD2] dark:border-gray-600 bg-white dark:bg-gray-800 shadow-2xl overflow-hidden"
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 20 }}
          transition={{ duration: 0.2, ease: 'easeOut' }}
          role="dialog"
          aria-label={t('quickActions.title', 'Quick Actions')}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-[#F0EBE3] dark:border-gray-700">
            <div className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-primary-500" />
              <h2 className="text-lg font-semibold text-[#001B3D] dark:text-gray-100">
                {t('quickActions.title', 'Quick Actions')}
              </h2>
            </div>
            <button
              onClick={handleClose}
              className="rounded-lg p-1.5 text-[#5C5C5C]/70 hover:text-[#5C5C5C] dark:hover:text-[#5C5C5C]/50 hover:bg-[#F0EBE3] dark:hover:bg-gray-700 transition-colors"
              aria-label={t('common.close', 'Close')}
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Actions grid */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 p-6">
            {ACTIONS.map((action, index) => {
              const Icon = action.icon;
              return (
                <motion.button
                  key={action.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  onClick={() => handleAction(action)}
                  className={cn(
                    'group flex flex-col items-center gap-3 rounded-xl p-4 border border-[#F0EBE3] dark:border-gray-700',
                    'hover:border-primary-200 dark:hover:border-primary-700 hover:shadow-md',
                    'transition-all duration-200 text-center'
                  )}
                >
                  <div
                    className={cn(
                      'flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br text-white shadow-sm',
                      action.color
                    )}
                  >
                    <Icon className="h-6 w-6" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-[#001B3D] dark:text-gray-100">
                      {t(action.label, action.label.split('.').pop())}
                    </p>
                    <p className="text-[11px] text-[#5C5C5C] dark:text-[#E8D5BC] mt-0.5">
                      {t(action.description, action.description.split('.').pop())}
                    </p>
                  </div>
                  {action.shortcut && (
                    <kbd className="inline-flex items-center rounded border border-[#E5DDD2] dark:border-gray-600 bg-[#FDFBF7] dark:bg-gray-700 px-1.5 py-0.5 text-[10px] font-mono text-[#5C5C5C] dark:text-[#E8D5BC]">
                      {action.shortcut}
                    </kbd>
                  )}
                </motion.button>
              );
            })}
          </div>

          {/* Footer hint */}
          <div className="px-6 py-3 border-t border-[#F0EBE3] dark:border-gray-700 text-center">
            <p className="text-[11px] text-[#5C5C5C]/70 dark:text-[#E8D5BC]">
              {t('quickActions.hint', 'Press')} <kbd className="font-mono mx-0.5">⌘⇧A</kbd> {t('quickActions.hintToggle', 'to toggle')}
            </p>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

export default QuickActions;
